import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getVisitorSource } from '@/lib/visitorSource';

/**
 * Tracks clicks on shared/affiliate links of the form ?ref=<code>.
 *
 * Each landing fires a "link_click" event with content_slug `affiliate:<code>`
 * so clicks can be counted per link code in the admin content views data.
 * Deduped once per browser session (so a reload does not double count),
 * then the ?ref parameter is stripped from the address bar.
 */

const SEEN_KEY = 'r0hub:affiliate_ref_seen';

export function useAffiliateLinkTracker() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get('ref');
      if (!raw) return;

      // Normalise the code: lowercase letters, numbers and hyphens only.
      const code = raw.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 50);
      if (!code) return;

      const day = new Date().toISOString().slice(0, 10);
      let alreadyCounted = false;
      try {
        const seen = sessionStorage.getItem(SEEN_KEY);
        alreadyCounted = seen === `${code}:${day}`;
        if (!alreadyCounted) sessionStorage.setItem(SEEN_KEY, `${code}:${day}`);
      } catch { /* storage unavailable - still track */ }

      if (!alreadyCounted) {
        // Capture the visitor source before the URL is cleaned.
        supabase.functions.invoke('track-content-view', {
          body: {
            content_type: 'link_click',
            content_slug: `affiliate:${code}`,
            ...getVisitorSource(),
          },
        }).catch(() => { /* silent */ });
      }

      // Strip ?ref so the shared code does not linger in the address bar.
      params.delete('ref');
      const qs = params.toString();
      const cleanUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    } catch { /* ignore */ }
  }, []);
}
